import React, { useEffect, useMemo, useState } from "react";
import { GoCreditCard } from "react-icons/go";
import { toast } from "react-toastify";
import DataTable from "./DataTable";
import StatusPill from "./StatusPill";
import { kpisCredito, fornecedoresCredito } from "../mockData";
import validacaoFiscalService from "../../../services/validacaoFiscalService";

const SUGESTAO_LABEL = { rad: "RAD", cobrar: "Cobrar", "rad-decidido": "RAD decidido" };
const SUGESTAO_VARIANT = { rad: "dark", cobrar: "neutral", "rad-decidido": "success" };

function parseMoeda(valor) {
  if (!valor) return 0;
  if (typeof valor === "number") return valor;
  return parseFloat(valor.replace("R$", "").replace(/\./g, "").replace(",", ".").trim()) || 0;
}

function formatCurrency(num) {
  return `R$ ${Number(num || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CreditoTravadoTab({ competenciaId }) {
  const [real, setReal] = useState(null);
  const [loading, setLoading] = useState(!!competenciaId);
  const [registrando, setRegistrando] = useState(false);

  const carregar = () => {
    if (!competenciaId) return;
    setLoading(true);
    validacaoFiscalService
      .obterCreditoTravado(competenciaId)
      .then((resp) => setReal(resp?.data?.data || null))
      .catch((err) => console.error("[CreditoTravadoTab] Erro:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (competenciaId) carregar();
    else setReal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competenciaId]);

  const usandoReal = !!competenciaId;
  const linhasReal = (real?.ranking || []).map((r) => ({
    fornecedor: r.fornecedorCnpj || "Fornecedor não identificado",
    cnpj: r.fornecedorCnpj,
    documentos: r.documentos,
    documentosIds: r.documentosIds,
    credito: formatCurrency(r.credito),
    recorrencia: `${r.recorrenciaMeses} competência(s)`,
    sugestao: r.sugestao,
  }));
  const linhas = usandoReal ? linhasReal : fornecedoresCredito;

  const simulaveis = linhas.filter((f) => f.sugestao === "rad" || f.sugestao === "cobrar");
  const [selecionado, setSelecionado] = useState(null);
  const atual = selecionado || simulaveis[0];

  useEffect(() => {
    setSelecionado(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competenciaId, real]);

  const simulacao = useMemo(() => {
    const valor = parseMoeda(atual?.credito);
    return {
      documentos: atual?.documentos || 0,
      valorARecolher: valor,
      creditoApos: valor,
      efeito: -valor,
    };
  }, [atual]);

  const handleRegistrarRad = async () => {
    if (!usandoReal || !atual?.documentosIds?.length) return;
    setRegistrando(true);
    try {
      await validacaoFiscalService.registrarDecisaoRad(competenciaId, {
        fornecedorCnpj: atual.cnpj,
        documentosIds: atual.documentosIds,
      });
      toast.success("Decisão de RAD registrada.");
      carregar();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao registrar RAD.");
    } finally {
      setRegistrando(false);
    }
  };

  const handleSaldoCredor = async (tipo) => {
    if (!usandoReal) return;
    try {
      await validacaoFiscalService.registrarSaldoCredor(competenciaId, { tipo });
      toast.success("Decisão registrada.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao registrar decisão.");
    }
  };

  const columns = [
    {
      key: "fornecedor",
      label: "Fornecedor",
      render: (row) => (
        <>
          {row.fornecedor}
          {row.cnpj && (
            <div style={{ fontSize: "0.72em", opacity: 0.55, fontFamily: "'JetBrains Mono', monospace" }}>
              {row.cnpj}
            </div>
          )}
        </>
      ),
    },
    { key: "documentos", label: "Documentos" },
    { key: "credito", label: "Crédito travado", render: (row) => <strong>{row.credito}</strong> },
    { key: "recorrencia", label: "Recorrência" },
    {
      key: "sugestao",
      label: "Sugestão",
      render: (row) =>
        row.sugestao ? (
          <button
            type="button"
            className="link-chave"
            style={{ textDecoration: "none" }}
            onClick={() => (row.sugestao !== "rad-decidido" ? setSelecionado(row) : undefined)}
            disabled={row.sugestao === "rad-decidido"}
          >
            <StatusPill variant={SUGESTAO_VARIANT[row.sugestao]}>{SUGESTAO_LABEL[row.sugestao]}</StatusPill>
          </button>
        ) : (
          "—"
        ),
    },
  ];

  const kpis = usandoReal
    ? [
        { label: "Total travado", valor: formatCurrency(real?.totalTravado) },
        { label: "Fornecedores", valor: String(real?.totalFornecedores || 0) },
        { label: "RAD já decidido nesta competência", valor: formatCurrency(real?.radDecididoNestaCompetencia) },
      ]
    : kpisCredito;

  return (
    <>
      <div className="section-title">
        <GoCreditCard />
        <h4>Crédito travado e RAD {loading && "(atualizando…)"}</h4>
      </div>
      <p className="table-footnote" style={{ maxWidth: "76ch", marginBottom: 16 }}>
        Créditos de compras cujo débito o fornecedor ainda não extinguiu. O crédito só é apropriado depois
        da extinção (art. 47); o recolhimento pelo adquirente (RAD) é a saída, sempre vinculado à operação
        (art. 36).
      </p>

      <div className="kpi-cards" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        {kpis.map((kpi) => (
          <div className="kpi-card" key={kpi.label}>
            <div className="kpi-label">{kpi.label}</div>
            <div className="kpi-value">{kpi.valor}</div>
          </div>
        ))}
      </div>

      <div className="ranking-layout">
        <div className="ranking-table-wrap">
          <DataTable title="Ranking de fornecedores por crédito travado" columns={columns} rows={linhas} />
        </div>

        <aside className="rad-sim-aside">
          <div className="rad-sim-title">Simular RAD · {atual?.fornecedor || "selecione um fornecedor"}</div>
          <div className="rad-sim-text">
            O RAD só paga débitos da operação indicada. O excedente volta ao fornecedor em até 3 dias
            úteis. Valor mínimo de R$ 10,00 no piloto (parâmetro).
          </div>
          <div className="rad-sim-row">
            <span>Documentos selecionados</span>
            <strong>{simulacao.documentos}</strong>
          </div>
          <div className="rad-sim-row">
            <span>Valor a recolher</span>
            <strong>{formatCurrency(simulacao.valorARecolher)}</strong>
          </div>
          <div className="rad-sim-row">
            <span>Crédito apropriado após o recolhimento</span>
            <strong>{formatCurrency(simulacao.creditoApos)}</strong>
          </div>
          <div className="rad-sim-row">
            <span>Efeito no saldo da AA</span>
            <strong>{formatCurrency(simulacao.efeito)} a pagar</strong>
          </div>
          <div className="rad-sim-note">
            Ordem de prioridade na extinção parcial (art. 26, § 2º, I): débitos que geram crédito ao
            adquirente vêm primeiro.
          </div>
          <button
            type="button"
            className="icon-btn primary"
            onClick={handleRegistrarRad}
            disabled={!usandoReal || registrando || !atual?.documentosIds?.length}
            title={usandoReal ? undefined : "Disponível com uma competência real aberta"}
          >
            {registrando ? "Registrando…" : "Registrar decisão de RAD"}
          </button>
        </aside>
      </div>

      <div className="saldo-credor-card">
        <div className="saldo-credor-text">
          <div className="saldo-credor-title">Saldo credor: compensar ou ressarcir?</div>
          <div className="saldo-credor-desc">
            Quando a apuração fecha credora, a decisão fica registrada. A intenção de ressarcimento
            reserva o saldo e bloqueia a compensação automática (art. 39); o pedido vai até o último dia
            útil do mês seguinte.
          </div>
        </div>
        <div className="saldo-credor-actions">
          <button type="button" className="icon-btn" onClick={() => handleSaldoCredor("compensar")} disabled={!usandoReal}>
            Manter para compensação
          </button>
          <button type="button" className="icon-btn" onClick={() => handleSaldoCredor("ressarcir")} disabled={!usandoReal}>
            Registrar intenção de ressarcimento
          </button>
        </div>
      </div>

      <div className="table-footnote">
        {usandoReal
          ? "Ranking calculado a partir dos créditos \"a apropriar\" registrados pela conciliação."
          : "Dados ilustrativos. A sugestão (RAD ou cobrar) vem de regras por cliente: recorrência, valor e relacionamento comercial."}
      </div>
    </>
  );
}
